import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Tooltip,
} from '@mui/material';
import { format } from 'date-fns';

export interface AddonHistoryItem {
  sessionId: string;
  purchaseDate: string;
  purchaseAmount: number;
  expiresAt?: number; // optional
  productId: string;
  customerID: string;
  clientId: string;
}

interface AddonHistoryProps {
  addonHistory: AddonHistoryItem[];
  isLoading?: boolean;
}

const AddonHistoryTable: React.FC<AddonHistoryProps> = ({ addonHistory, isLoading = false }) => {
  const skeletonRows = 2;

  if (isLoading) {
    return (
      <Box mt={6}>
        <Skeleton variant="text" width="30%" height={40} sx={{ mb: 2 }} />
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                {['Session ID', 'Purchase Date', 'Amount ($)', 'Expires At'].map(header => (
                  <TableCell key={header}>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from(new Array(skeletonRows)).map((_, index) => (
                <TableRow key={index}>
                  {Array.from(new Array(4)).map((_, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  if (!addonHistory?.length) {
    return (
      <Box mt={4}>
        <Typography variant="h6" color="textSecondary">
          No addon history available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box mt={6}>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Addon Purchase History
      </Typography>
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Session ID</TableCell>
              <TableCell>Purchase Date</TableCell>
              <TableCell>Amount ($)</TableCell>
              <TableCell>Expires At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {addonHistory.map(item => (
              <TableRow key={item.sessionId}>
                <TableCell>
                  <Tooltip title={item.sessionId}>
                    <span>{item.sessionId.slice(0, 10)}...</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{format(new Date(item.purchaseDate), 'PPP p')}</TableCell>
                <TableCell>${item.purchaseAmount.toFixed(2)}</TableCell>
                <TableCell>
                  {item.expiresAt ? format(new Date(item.expiresAt * 1000), 'PPP p') : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AddonHistoryTable;
